import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createInventoryCategory,
  deleteInventoryCategory,
  listInventoryCategories,
  patchInventoryCategory,
  type InventoryCategoryNode,
} from '@/lib/api/inventory-categories.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

function CategoryRow({
  node,
  depth,
  onChanged,
}: {
  node: InventoryCategoryNode;
  depth: number;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(node.name);

  const saveMutation = useMutation({
    mutationFn: () => patchInventoryCategory(node.id, { name }),
    onSuccess: () => {
      toastSuccess('Category updated');
      setEditing(false);
      onChanged();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteInventoryCategory(node.id),
    onSuccess: () => {
      toastSuccess('Category deleted');
      onChanged();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const addChildMutation = useMutation({
    mutationFn: () =>
      createInventoryCategory({
        name: `New under ${node.name}`,
        parentId: node.id,
      }),
    onSuccess: () => {
      toastSuccess('Child category added');
      onChanged();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  return (
    <div>
      <div
        className="flex items-center gap-2 border-b border-slate-100 py-2"
        style={{ paddingLeft: depth * 16 }}
      >
        {editing ? (
          <>
            <input
              className="flex-1 rounded-lg border px-2 py-1 text-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <button
              type="button"
              className="text-sm font-semibold text-primary"
              onClick={() => saveMutation.mutate()}
            >
              Save
            </button>
            <button type="button" className="text-sm text-slate-500" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <span className="flex-1 text-sm font-medium text-slate-900">{node.name}</span>
            <button type="button" className="text-xs font-semibold text-slate-600" onClick={() => setEditing(true)}>
              Rename
            </button>
            <button
              type="button"
              className="text-xs font-semibold text-slate-600"
              onClick={() => addChildMutation.mutate()}
            >
              Add child
            </button>
            <button
              type="button"
              className="text-xs font-semibold text-red-600"
              onClick={() => {
                if (window.confirm(`Delete “${node.name}”?`)) deleteMutation.mutate();
              }}
            >
              Delete
            </button>
          </>
        )}
      </div>
      {(node.children ?? []).map((child) => (
        <CategoryRow key={child.id} node={child} depth={depth + 1} onChanged={onChanged} />
      ))}
    </div>
  );
}

export function InventoryCategoriesSettingsPage() {
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const query = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: listInventoryCategories,
  });

  const createMutation = useMutation({
    mutationFn: () => createInventoryCategory({ name: newName.trim() }),
    onSuccess: () => {
      setNewName('');
      toastSuccess('Category created');
      queryClient.invalidateQueries({ queryKey: ['inventory-categories'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['inventory-categories'] });
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-950">Inventory Categories</h2>
        <p className="mt-1 text-sm text-slate-500">
          Manage the hierarchical category tree used on inventory items.
        </p>
      </div>

      <form
        className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
        onSubmit={(e) => {
          e.preventDefault();
          if (!newName.trim()) return;
          createMutation.mutate();
        }}
      >
        <input
          className="min-w-[220px] flex-1 rounded-xl border border-slate-300 px-3 py-2"
          placeholder="New root category name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <button
          type="submit"
          disabled={createMutation.isPending || !newName.trim()}
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          Add root
        </button>
      </form>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        {query.isLoading ? <p className="text-sm text-slate-500">Loading…</p> : null}
        {(query.data ?? []).map((node) => (
          <CategoryRow key={node.id} node={node} depth={0} onChanged={refresh} />
        ))}
        {!query.isLoading && (query.data ?? []).length === 0 ? (
          <p className="text-sm text-slate-500">No categories yet.</p>
        ) : null}
      </div>
    </div>
  );
}
